import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Box,
  Tabs,
  Tab,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddUserModal, { canAssignRole } from "./AddUserModal";
import { Add as AddIcon } from "@mui/icons-material";
import axios from "axios";
import { useToast } from "../../../../components/Toast";
import { Loading } from "../../../../components/Loading";
import {
  commonButtonProps,
  tableCellStyles,
  tableHeaderStyles,
} from "../../../../utils/styles";
import { projectUserService } from "../../../../project/project_module/services/projectUserService";
import { getValidToken } from "../../../../utils/authUtils";
import { UserScope } from "../../../../types/operationType";
import { PROJECT_TERMINOLOGY } from "../../../../project/project_module/enums/terminologyConfig";
import { usePlanStatus } from "../../../../hooks/usePlanStatus";
import { PlanStatus } from "../../../../utils/alertUtils";

// Define the user type
export interface User {
  id?: string;
  name: string;
  role: string;
  phoneNumber: string;
  email?: string;
}

const UserTab = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [projectUsers, setProjectUsers] = useState<User[]>([]);
  const { showToast } = useToast();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const localUser = sessionStorage.getItem("user");
  const companyId = localUser ? JSON.parse(localUser).companyId : null;
  const [tab, setTab] = useState("global");
  const { planStatus } = usePlanStatus();
  useEffect(() => {
    setIsLoading(true);
    if (companyId) {
      if (tab === "global") {
        getUsers();
      } else {
        getProjectUsers();
      }
    }
  }, [companyId, tab]);

  const getUsers = async () => {
    try {
      const token = await getValidToken();
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST}users/company/${companyId}/global-scope-users`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data?.isSuccess) {
        const filteredUsers = response.data?.data.filter(
          (user: User) => user.role !== "ADMIN"
        );
        setUsers(filteredUsers);
      } else {
        // Handle case where isSuccess is false but no exception was thrown
        if (response.data.message) {
          throw new Error(response.data.message);
        }
        if (response.data.error) {
          throw new Error(response.data.error);
        }
        throw new Error("Failed to fetch users");
      }
    } catch (error: any) {
      console.error("Error fetching users:", error);
      // Extract error message from axios error response
      if (error.response?.data?.message) {
        showToast(error.response.data.message, "error");
      } else if (error.response?.data?.error) {
        showToast(error.response.data.error, "error");
      } else {
        showToast(error?.message || "Failed to fetch users", "error");
      }
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getProjectUsers = async () => {
    try {
      const response = await projectUserService.getAllProjectUsers(companyId);
      if (response) {
        setProjectUsers(response);
      } else {
        setProjectUsers([]);
      }
    } catch (error: any) {
      console.error("Error fetching project users:", error);
      showToast(error?.message || "Failed to fetch project users", "error");
      setProjectUsers([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddUser = () => {
    setSelectedUser(undefined);
    setIsModalOpen(true);
  };

  const handleEdit = (user: User) => {
    if (canAssignRole(planStatus?.user?.userRole?.roleCode, user?.role)) {
      setSelectedUser(user);
      setIsModalOpen(true);
    } else {
      showToast("You are not authorized to edit this user", "warning");
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setTab(newValue);
    // The useEffect will handle fetching data for the new tab
  };

  if (isLoading) {
    return <Loading message="Loading Users..." fullScreen={true} />;
  }

  return (
    <>
      {/* 2 tabs for company users and project users */}
      <Tabs
        value={tab}
        onChange={handleTabChange}
        aria-label="User tabs"
        sx={{
          "& .MuiTab-root": {
            fontSize: "0.75rem",
            py: 0,
            lineHeight: "1",
          },
        }}
      >
        <Tab label="Company Users" value="global" />
        <Tab label={PROJECT_TERMINOLOGY.SINGULAR + " Users"} value="project" />
      </Tabs>
      {tab === "global" && planStatus?.permissions?.canCreateUser && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", my: 1 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddUser}
            {...commonButtonProps}
            startIcon={<AddIcon fontSize="small" />}
          >
            Add User
          </Button>
        </Box>
      )}

      {tab === "global"
        ? renderUsers(users, handleEdit, tab, planStatus)
        : renderUsers(projectUsers, handleEdit, tab, planStatus)}

      <AddUserModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={() => {
          setIsModalOpen(false);
          if (tab === "global") {
            getUsers();
          } else {
            getProjectUsers();
          }
        }}
        editUser={selectedUser}
        userScope={tab === "global" ? UserScope.GLOBAL : UserScope.PROJECT}
        planStatus={planStatus}
      />
    </>
  );
};

export default UserTab;

const renderUsers = (
  users: User[],
  handleEdit: (user: User) => void,
  tab: string,
  planStatus: PlanStatus
) => {
  return (
    <TableContainer component={Paper} sx={{ mt: tab === "global" ? 0 : 1 }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...tableHeaderStyles, py: 1 }}>Name</TableCell>
            {tab === "global" && (
              <TableCell sx={{ ...tableHeaderStyles, py: 1 }}>Role</TableCell>
            )}
            <TableCell sx={{ ...tableHeaderStyles, py: 1 }}>Phone</TableCell>
            <TableCell sx={{ ...tableHeaderStyles, py: 1 }}>Email</TableCell>
            {planStatus?.permissions?.canEditUser && (
              <TableCell sx={{ ...tableHeaderStyles, py: 1 }}>
                Actions
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {users && users?.length > 0 ? (
            users?.map((user, index) => (
              <TableRow key={index}>
                <TableCell sx={{ ...tableCellStyles, py: 1 }}>
                  {user?.name}
                </TableCell>
                {tab === "global" && (
                  <TableCell sx={{ ...tableCellStyles, py: 1 }}>
                    {user?.role || "-"}
                  </TableCell>
                )}
                <TableCell sx={{ ...tableCellStyles, py: 1 }}>
                  {user?.phoneNumber}
                </TableCell>
                <TableCell sx={{ ...tableCellStyles, py: 1 }}>
                  {user?.email || "-"}
                </TableCell>
                {planStatus?.permissions?.canEditUser && (
                  <TableCell sx={{ ...tableCellStyles, py: 1 }}>
                    <IconButton
                      onClick={() => handleEdit(user)}
                      size="small"
                      color="primary"
                    >
                      <EditIcon style={{ fontSize: "0.75rem" }} />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={7}
                align="center"
                sx={{ ...tableCellStyles, py: 1 }}
              >
                No users found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
